import { useEffect } from "react";
import { createPortal } from "react-dom";

const REPORT_WHATSAPP_ENABLED = false;

const defaultPreview =
  "I felt much lighter after today’s therapy and slept peacefully for the first time this week. The therapist was very kind and understanding.";

const reportItems = [
  {
    label: "Instant summary",
    image: "/assets/screen8-assets/Instant_summary.webp",
  },
  {
    label: "Wellness score",
    image: "/assets/screen8-assets/wellness_score.webp",
  },
  {
    label: "Personalised recommendations",
    image: "/assets/screen8-assets/personalised_recommendation.webp",
  },
  {
    label: "Upcoming appointment details",
    image: "/assets/screen8-assets/upcoming_appointment.webp",
  },
];
function createReportUrl(feedback, patient) {
  const params = new URLSearchParams();
  params.set("mood", feedback.mood);
  params.set("rating", feedback.therapyRating);
  params.set("factors", feedback.factors.join(","));
  params.set("before", feedback.stressBefore);
  params.set("after", feedback.stressAfter);
  params.set("improvements", feedback.improvements.join(","));

  if (feedback.factors.includes("OTHER") && feedback.otherFactor.trim()) {
    params.set("otherFactor", feedback.otherFactor.trim());
  }

  if (
    feedback.improvements.includes("OTHER") &&
    feedback.improvementOther.trim()
  ) {
    params.set("improvementOther", feedback.improvementOther.trim());
  }

  Object.entries(feedback.experience).forEach(([question, rating]) => {
    params.set(question, rating);
  });

  if (feedback.comment.trim()) {
    params.set("comment", feedback.comment.trim());
  }

  params.set("appointment_id", patient?.hospital_appointment_id || "");
  return `/patient-feedback-report/${encodeURIComponent(patient?.patient_id || "")}?${params.toString()}`;
}

function CompletionModal({ feedback, patient, onClose }) {
  useEffect(() => {
    function closeOnEscape(event) {
      if (event.key === "Escape") onClose();
    }

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  function openReport() {
    window.open(createReportUrl(feedback, patient), "_blank", "noopener,noreferrer");
  }

  return createPortal(
    <div className="completion-modal">
      <section
        className="completion-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="completion-title"
      >
        <input
          className="completion-modal__close"
          type="button"
          name="ui_action"
          value="×"
          aria-label="Close thank you popup"
          onClick={onClose}
        />
        <div className="completion-modal__icon" aria-hidden="true">
          ✓
        </div>
        <span className="completion-modal__kicker">FEEDBACK COMPLETED</span>
        <h2 id="completion-title">Thank you for sharing your experience.</h2>
        <p>
          Your feedback has been securely saved with your patient record.
        </p>

        <div className="completion-modal__summary">
          <div>
            <span>Patient ID</span>
            <strong>{patient?.patient_id || "-"}</strong>
          </div>
          <div>
            <span>Therapy rating</span>
            <strong>{feedback.therapyRating} / 5</strong>
          </div>
          <div>
            <span>Appointment</span>
            <strong>{patient?.appointment_date || "-"} {patient?.appointment_time || ""}</strong>
          </div>
        </div>

        <div className={`completion-modal__actions${REPORT_WHATSAPP_ENABLED ? "" : " is-single"}`}>
          <input
            className="screen-eight__action screen-eight__action--primary"
            type="button"
            name="ui_action"
            value="View Full Report"
            onClick={openReport}
          />
          {REPORT_WHATSAPP_ENABLED && (
            <input
              className="screen-eight__action screen-eight__action--whatsapp"
              type="button"
              name="ui_action"
              value="Send Report on WhatsApp"
            />
          )}
        </div>

        <div className="completion-modal__access">
          <div className="screen-eight__qr" aria-hidden="true" />
          <div>
            <strong>Open on another device</strong>
            <span>QR is a visual demo only.</span>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}

const ScreenEight = ({
  feedback,
  patient,
  onCommentChange,
  completionOpen,
  onSubmit,
  onCloseCompletion,
  submitting,
  submitError,
}) => {

  const preview = feedback.comment.trim() || defaultPreview;

  function openReport() {
    window.open(createReportUrl(feedback, patient), "_blank", "noopener,noreferrer");
  }

  return (
    <section className="screen-eight" aria-labelledby="screen-eight-title">
      <aside className="screen-eight__intro left-side" aria-hidden="true">
        <div className="screen-eight__intro-title">
          <strong>Patient</strong>
          <span>Feedback</span>
        </div>
        <img
          className="screen-eight__intro-divider"
          src="/assets/screen4-assets/line.webp"
          alt=""
        />
        <p>Your feedback helps us serve you better.</p>
      </aside>

      <div className="content-side full-screen">
        <div className="screen-eight__main main-content">
        <img
          className="screen-eight__deco screen-eight__deco--left"
          src="/assets/screen8-assets/leaf_01.webp"
          alt=""
          aria-hidden="true"
        />
        <img
          className="screen-eight__deco screen-eight__deco--right"
          src="/assets/screen8-assets/leaf.webp"
          alt=""
          aria-hidden="true"
        />

        <h1 id="screen-eight-title">Patient Feedback</h1>
        <img
          className="screen-eight__title-lotus"
          src="/assets/screen8-assets/Golden_Leaf.webp"
          alt=""
          aria-hidden="true"
        />
        <p className="screen-eight__subtitle">
          Your feedback helps us serve you better.
        </p>

        <blockquote className="screen-eight__preview" aria-live="polite">
          “ {preview} ”
        </blockquote>

        <div className="screen-eight__comment">
          <label htmlFor="final-feedback-comment">
            Would you like to add anything else?
          </label>
          <textarea
            id="final-feedback-comment"
            name="comment"
            maxLength="1000"
            placeholder="Write your feedback here..."
            value={feedback.comment}
            onChange={(event) => onCommentChange(event.target.value)}
          />
          <small>{feedback.comment.length} characters</small>
        </div>

        <section className="report-access" aria-labelledby="report-access-title">
          <img
            className="report-access__leaf"
            src="/assets/screen8-assets/leaf_2.webp"
            alt=""
            aria-hidden="true"
          />
          <h2 id="report-access-title">Access Your Report Anytime</h2>
          <img
            className="report-access__divider"
            src="/assets/screen8-assets/golden_leaf_2.webp"
            alt=""
            aria-hidden="true"
          />

          <div className={`report-access__options${REPORT_WHATSAPP_ENABLED ? "" : " is-single"}`}>
            {REPORT_WHATSAPP_ENABLED && <article className="report-access__option">
              <div className="report-access__icon">
                <img
                  src="/assets/screen8-assets/golden_circle.webp"
                  alt=""
                  aria-hidden="true"
                />
                <span aria-hidden="true">◉</span>
              </div>
              <div>
                <p>
                  Send your report
                  <br />
                  instantly on WhatsApp
                </p>
                <input
                  className="screen-eight__action screen-eight__action--primary"
                  type="button"
                  name="ui_action"
                  value="Send on WhatsApp"
                />
              </div>
            </article>}

            <article className="report-access__option">
              <div className="report-access__icon">
                <img
                  src="/assets/screen8-assets/golden_circle.webp"
                  alt=""
                  aria-hidden="true"
                />
                <div className="screen-eight__qr" aria-hidden="true" />
              </div>
              <div>
                <p>
                  Scan to view or
                  <br />
                  download your
                  <br />
                  full report
                </p>
                <input
                  className="screen-eight__action screen-eight__action--primary"
                  type="button"
                  name="ui_action"
                  value="View Full Report"
                  onClick={openReport}
                />
              </div>
            </article>
          </div>

          <div className="report-access__contents" aria-label="Report includes">
            {reportItems.map((item) => (
              <div key={item.label}>
                <img src={item.image} alt="" aria-hidden="true" />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <input
            className="screen-eight__complete"
            type="button"
            name="ui_action"
            value={submitting ? "Saving Feedback..." : "Complete & Submit"}
            onClick={onSubmit}
            disabled={submitting}
          />
          {submitError && <p className="screen-eight__submit-error" role="alert">{submitError}</p>}
        </section>

        <p className="screen-eight__privacy">
          ▣ &nbsp; Your feedback is secure and confidential
        </p>
        </div>
      </div>

      {completionOpen && (
        <CompletionModal feedback={feedback} patient={patient} onClose={onCloseCompletion} />
      )}
    </section>
  );
}

export default ScreenEight;
